import type { AssetKind, AssetWithUrls, Folder, Id, Paginated } from '@shared/types';

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
  get isAuthError() {
    return this.status === 401;
  }
}

const CSRF = { 'X-Requested-With': 'ecomstat-dam' } as const;

async function request<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const { method = 'GET', body } = opts;
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(method === 'GET' ? {} : CSRF),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const e = (await res.json()) as { error?: string };
      if (e.error) msg = e.error;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(res.status, msg);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export interface TaggedAssetWithUrls extends AssetWithUrls {
  aiTags: {
    brand: string;
    productTitle: string;
    imageType: string;
    description: string;
    hashtags: string;
    confirmed: boolean;
  } | null;
}

export interface AssetFilter {
  folderId?: Id;
  recursive?: boolean;
  kind?: AssetKind;
  search?: string;
  tag?: string;
  untagged?: boolean;
  favourites?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

function qs(params: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const api = {
  me: () => request<{ user: { id: number; username: string; name: string; email: string | null; isAdmin: boolean } }>('/me'),
  login: (username: string, password: string) =>
    request<{ user: unknown }>('/auth/login', { method: 'POST', body: { username, password } }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  // Magic-link (passwordless) login
  requestMagicLink: (email: string) => request<{ ok: true; devLink?: string }>('/auth/magic/request', { method: 'POST', body: { email } }),
  consumeMagicToken: (token: string) => request<{ user: unknown }>('/auth/magic/consume', { method: 'POST', body: { token } }),
  createShareLink: (userId?: number) => request<{ link: string }>('/auth/share', { method: 'POST', body: userId ? { userId } : {} }),
  sendTestEmail: () => request<{ ok: true }>('/auth/test-email', { method: 'POST' }),

  folders: () => request<{ items: Folder[]; tree: Folder[] }>('/folders'),
  createFolder: (name: string, parentId: Id | null) => request<{ folder: Folder }>('/folders', { method: 'POST', body: { name, parentId } }),
  renameFolder: (id: Id, name: string) => request<{ ok: true }>(`/folders/${id}`, { method: 'PATCH', body: { name } }),
  deleteFolder: (id: Id) => request<{ ok: true }>(`/folders/${id}`, { method: 'DELETE' }),

  assets: (f: AssetFilter) => request<Paginated<AssetWithUrls>>(`/assets${qs(f as Record<string, unknown>)}`),
  asset: (id: Id) => request<{ asset: AssetWithUrls }>(`/assets/${id}`),
  updateMetadata: (id: Id, metadata: Record<string, unknown>) => request<{ asset: AssetWithUrls }>(`/assets/${id}/metadata`, { method: 'PATCH', body: metadata }),
  moveAsset: (id: Id, folderId: Id | null) => request<{ ok: true }>(`/assets/${id}/move`, { method: 'POST', body: { folderId } }),
  deleteAsset: (id: Id) => request<{ ok: true }>(`/assets/${id}`, { method: 'DELETE' }),

  upload: async (folderId: Id | null, file: File) => {
    const form = new FormData();
    form.append('file', file);
    if (folderId) form.append('folderId', String(folderId));
    const res = await fetch('/api/upload', { method: 'POST', credentials: 'same-origin', headers: { ...CSRF }, body: form });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, e.error ?? res.statusText);
    }
    return (await res.json()) as { asset: AssetWithUrls };
  },

  // AI tagging
  tagNow: (id: Id) => request<{ tags: unknown }>(`/assets/${id}/tag`, { method: 'POST' }),
  confirmTags: (id: Id, confirmed: boolean) => request<{ ok: true }>(`/assets/${id}/tag/confirm`, { method: 'POST', body: { confirmed } }),
  getPrompt: () => request<{ prompt: string }>('/tags/prompt'),
  setPrompt: (prompt: string) => request<{ ok: true }>('/tags/prompt', { method: 'PUT', body: { prompt } }),
  enqueueUntagged: () => request<{ enqueued: number }>('/tags/enqueue-untagged', { method: 'POST' }),
  drain: (limit = 10) => request<{ done: number; failed: number }>(`/tags/drain?limit=${limit}`, { method: 'POST' }),
  queueStats: () => request<{ stats: Record<string, number> }>('/tags/queue'),

  // meta-tag search
  search: (params: Record<string, unknown>) => request<Paginated<TaggedAssetWithUrls>>(`/search${qs(params)}`),
  facets: () => request<{ brands: string[]; imageTypes: string[] }>('/search/facets'),

  // library stats + favourites
  libraryStats: () => request<LibraryStats>('/library/stats'),
  favouriteIds: () => request<{ ids: number[] }>('/favourites/ids'),
  toggleFavourite: (id: Id) => request<{ favourited: boolean }>(`/assets/${id}/favourite`, { method: 'POST' }),

  // users (admin)
  users: () => request<{ items: DamUser[] }>('/users'),
  setUserActive: (id: Id, isActive: boolean) => request<{ ok: true }>(`/users/${id}/active`, { method: 'POST', body: { isActive } }),
  setUserAdmin: (id: Id, isAdmin: boolean) => request<{ ok: true }>(`/users/${id}/admin`, { method: 'POST', body: { isAdmin } }),
};

export interface LibraryStats {
  total: number;
  byKind: Record<string, number>;
  untagged: number;
  favourites: number;
  storageBytes: number;
}

export interface DamUser {
  id: number;
  username: string;
  name: string;
  email: string | null;
  isAdmin: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}
